import financialApi from "api/requests/financial";
import Loader from "components/_UI/Loader";
import Text from "components/_UI/Text";
import currency from "helpers/currency";
import React, { useEffect, useMemo, useState } from "react";
import ReactCalendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Calendar, CheckCircle, Clock } from "react-feather";
import styled, { keyframes } from "styled-components";
import { FullyCentered } from "styled/alignment/Center";
import { Column } from "styled/alignment/Column";
import { Row } from "styled/alignment/Row";
import breakpoints from "theme/breakpoints";
import colors from "theme/colors";
import fonts from "theme/fonts";

export default function ReceiptsCalendar() {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchMonth, setFetchMonth] = useState(today.getMonth());
  const [fetchYear, setFetchYear] = useState(today.getFullYear());

  useEffect(() => {
    getReceiptSchedule();
  }, [fetchMonth, fetchYear]);

  async function getReceiptSchedule() {
    setLoading(true);
    try {
      const { data } = await financialApi.receiptSchedule(
        fetchMonth + 1,
        fetchYear
      );
      setContent(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  const { totalReleased, totalPending } = useMemo(() => {
    if (!content || !content.length) return { totalReleased: 0, totalPending: 0 };

    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    return content.reduce(
      (acc, item) => {
        const amount = parseFloat(item.total_amount) || 0;
        if (item.payment_day <= todayStr) {
          acc.totalReleased += amount;
        } else {
          acc.totalPending += amount;
        }
        return acc;
      },
      { totalReleased: 0, totalPending: 0 }
    );
  }, [content]);

  function formatDateString(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const selectedEvent = useMemo(() => {
    if (!selectedDate || !content) return null;
    const dateStr = formatDateString(selectedDate);
    return content.find((item) => item.payment_day === dateStr) || null;
  }, [selectedDate, content]);

  return (
    <CalendarContainer>
      <SectionHeader>
        <SectionIcon>
          <Calendar size={18} color={colors.occasionalPurple} />
        </SectionIcon>
        <HeaderContent>
          <Text type="bold" color={colors.nightDark}>
            Agenda de recebimentos
          </Text>
          <Text name="small" color={colors.night}>
            Acompanhe seus recebimentos por dia
          </Text>
        </HeaderContent>
      </SectionHeader>

      {loading ? (
        <FullyCentered height="300px">
          <Loader />
        </FullyCentered>
      ) : (
        <>
          <SummaryRow>
            <SummaryCard variant="success">
              <SummaryIconBox variant="success">
                <CheckCircle size={18} color={colors.neutralLight} />
              </SummaryIconBox>
              <SummaryInfo>
                <SummaryLabel>Liberado</SummaryLabel>
                <SummaryValue color={colors.successDark}>
                  {currency.format(totalReleased)}
                </SummaryValue>
              </SummaryInfo>
            </SummaryCard>

            <SummaryCard variant="pending">
              <SummaryIconBox variant="pending">
                <Clock size={18} color={colors.neutralLight} />
              </SummaryIconBox>
              <SummaryInfo>
                <SummaryLabel>A liberar</SummaryLabel>
                <SummaryValue color="#e07800">
                  {currency.format(totalPending)}
                </SummaryValue>
              </SummaryInfo>
            </SummaryCard>
          </SummaryRow>

          <CalendarWrapper>
            <ReactCalendar
              value={currentDate}
              onChange={(date) => {
                setSelectedDate(date);
                setCurrentDate(date);
                setFetchMonth(date.getMonth());
                setFetchYear(date.getFullYear());
              }}
              onActiveStartDateChange={({ activeStartDate, view }) => {
                if (activeStartDate) {
                  setCurrentDate(activeStartDate);
                  if (view === "month") {
                    setFetchMonth(activeStartDate.getMonth());
                    setFetchYear(activeStartDate.getFullYear());
                  }
                }
              }}
              tileContent={({ date, view }) => {
                if (view !== "month") return null;

                const dateStr = formatDateString(date);
                const event = content?.find(
                  (item) => item.payment_day === dateStr
                );

                if (!event) return null;

                const isPast = dateStr <= formatDateString(today);

                return (
                  <TileBody>
                    <AmountBadge isPast={isPast}>
                      {currency.format(event.total_amount)}
                    </AmountBadge>
                    <StatusDot isPast={isPast} />
                  </TileBody>
                );
              }}
            />
          </CalendarWrapper>

          {selectedEvent && (
            <SelectedDetail>
              <DetailDate>
                {selectedDate.toLocaleDateString("pt-BR", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
              </DetailDate>
              <DetailAmount
                isPast={formatDateString(selectedDate) <= formatDateString(today)}
              >
                {currency.format(selectedEvent.total_amount)}
              </DetailAmount>
              <DetailStatus
                isPast={formatDateString(selectedDate) <= formatDateString(today)}
              >
                {formatDateString(selectedDate) <= formatDateString(today)
                  ? "Liberado"
                  : "A liberar"}
              </DetailStatus>
            </SelectedDetail>
          )}

          <Legend>
            <LegendItem>
              <StatusDot isPast />
              <LegendLabel>Liberado</LegendLabel>
            </LegendItem>
            <LegendItem>
              <StatusDot />
              <LegendLabel>A liberar</LegendLabel>
            </LegendItem>
          </Legend>
        </>
      )}
    </CalendarContainer>
  );
}

/* ── Animations ───────────────────────────────────── */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ── Container ────────────────────────────────────── */

const CalendarContainer = styled(Column)`
  background: ${colors.neutralLight};
  border: 1px solid ${colors.neutralDark};
  border-radius: 14px;
  padding: 20px 16px;
  margin-top: 10px;
  animation: ${fadeIn} 0.35s ease forwards;

  @media screen and (min-width: ${breakpoints.md}) {
    padding: 28px;
  }
`;

/* ── Header ───────────────────────────────────────── */

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #F3EAFF;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

/* ── Summary Row ──────────────────────────────────── */

const SummaryRow = styled(Row)`
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 160px;
  background: ${colors.neutralLight};
  border: 1px solid ${colors.neutralDark};
  border-radius: 12px;
  padding: 14px 16px;
  transition: all 0.2s ease;
  border-left: 3px solid ${({ variant }) =>
    variant === "success" ? colors.success : colors.occasionalOrange};

  &:hover {
    box-shadow: 0 2px 12px ${({ variant }) =>
      variant === "success"
        ? "rgba(43, 200, 102, 0.12)"
        : "rgba(255, 145, 35, 0.12)"};
    transform: translateY(-1px);
  }
`;

const SummaryIconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${({ variant }) =>
    variant === "success"
      ? `linear-gradient(135deg, ${colors.successLight}, ${colors.success})`
      : `linear-gradient(135deg, #ffb347, ${colors.occasionalOrange})`};
  flex-shrink: 0;
  box-shadow: ${({ variant }) =>
    variant === "success"
      ? "0 3px 8px rgba(43, 200, 102, 0.25)"
      : "0 3px 8px rgba(255, 145, 35, 0.25)"};
`;

const SummaryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SummaryLabel = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${colors.night};
  letter-spacing: 0.2px;
`;

const SummaryValue = styled.span`
  font-size: ${fonts.sizes.highlight};
  font-weight: ${fonts.weight.bold};
  color: ${({ color }) => color || colors.nightDark};
  letter-spacing: -0.3px;
`;

/* ── Calendar Wrapper ─────────────────────────────── */

const CalendarWrapper = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${colors.neutralDark};
  background: ${colors.neutralLight};

  .react-calendar {
    border: none;
    width: 100%;
    background: transparent;
    font-family: inherit;
  }

  /* Navigation */
  .react-calendar__navigation {
    margin: 0;
    padding: 8px 4px;
    background: ${colors.neutral};
    border-bottom: 1px solid ${colors.neutralDark};
    display: flex;
    align-items: center;
  }

  .react-calendar__navigation button {
    min-width: 36px;
    height: 36px;
    border-radius: 8px;
    background: none;
    color: ${colors.nightDark};
    font-weight: ${fonts.weight.bold};
    transition: all 0.15s ease;

    &:hover,
    &:focus {
      background: ${colors.neutralLight};
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }

    &:disabled {
      opacity: 0.3;
    }
  }

  .react-calendar__navigation__label {
    flex-grow: 1 !important;
  }

  .react-calendar__navigation__label__labelText {
    font-size: ${fonts.sizes.highlight};
    font-weight: ${fonts.weight.bold};
    color: ${colors.secondary};
    text-transform: capitalize;
    letter-spacing: -0.2px;
  }

  .react-calendar__navigation__prev-button,
  .react-calendar__navigation__next-button {
    font-size: 20px;
    color: ${colors.secondary};
  }

  .react-calendar__navigation__prev2-button,
  .react-calendar__navigation__next2-button {
    display: none !important;
  }

  /* Weekday headers */
  .react-calendar__month-view__weekdays {
    padding: 10px 0 8px;
    border-bottom: 1px solid ${colors.neutralDark};
    text-align: center;

    abbr {
      text-decoration: none;
      font-size: ${fonts.sizes.mini};
      font-weight: ${fonts.weight.bold};
      color: ${colors.night};
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  /* Day tiles */
  .react-calendar__tile {
    position: relative;
    padding: 6px 4px;
    min-height: 68px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border: none;
    border-bottom: 1px solid ${colors.neutralDark};
    border-right: 1px solid ${colors.neutralDark};
    transition: background 0.15s ease;
    background: transparent;

    @media screen and (min-width: ${breakpoints.sm}) {
      min-height: 78px;
    }

    @media screen and (min-width: ${breakpoints.md}) {
      min-height: 88px;
    }

    &:hover {
      background: ${colors.neutral};
    }
  }

  .react-calendar__tile abbr {
    font-weight: ${fonts.weight.bold};
    font-size: ${fonts.sizes.small};
    color: ${colors.nightDark};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    transition: all 0.15s ease;
  }

  /* Last column remove right border */
  .react-calendar__month-view__days__day:nth-child(7n) {
    border-right: none;
  }

  /* Last row remove bottom border */
  .react-calendar__month-view__days .react-calendar__tile:nth-last-child(-n+7) {
    border-bottom: none;
  }

  /* Today */
  .react-calendar__tile--now {
    background: transparent;

    abbr {
      background: ${colors.secondary};
      color: ${colors.neutralLight};
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(21, 17, 54, 0.2);
    }

    &:hover {
      background: ${colors.neutral};
    }
  }

  /* Active / selected */
  .react-calendar__tile--active {
    background: rgba(63, 107, 198, 0.06) !important;

    abbr {
      background: ${colors.primary};
      color: ${colors.neutralLight};
      border-radius: 8px;
    }
  }

  /* Neighboring months */
  .react-calendar__month-view__days__day--neighboringMonth {
    abbr {
      color: ${colors.nightLight} !important;
    }
    opacity: 0.45;
  }

  .react-calendar__month-view__days__day--weekend abbr {
    color: ${colors.nightDark};
  }

  /* Year/decade view tiles */
  .react-calendar__year-view .react-calendar__tile,
  .react-calendar__decade-view .react-calendar__tile,
  .react-calendar__century-view .react-calendar__tile {
    min-height: 48px;
    border-radius: 8px;
    border: none;
    padding: 12px 8px;

    &:hover {
      background: ${colors.neutral};
    }
  }

  .react-calendar__year-view .react-calendar__tile--now,
  .react-calendar__decade-view .react-calendar__tile--now {
    background: rgba(21, 17, 54, 0.06);
    border-radius: 8px;

    abbr {
      background: none;
      color: ${colors.secondary};
      box-shadow: none;
      width: auto;
      height: auto;
    }
  }

  .react-calendar__year-view .react-calendar__tile--active,
  .react-calendar__decade-view .react-calendar__tile--active {
    background: ${colors.primary} !important;
    border-radius: 8px;

    abbr {
      color: ${colors.neutralLight};
      background: none;
    }
  }
`;

/* ── Tile Content ─────────────────────────────────── */

const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  margin-top: 2px;
  animation: ${fadeIn} 0.25s ease forwards;
`;

const AmountBadge = styled.span`
  font-size: ${fonts.sizes.mini};
  font-weight: ${fonts.weight.bold};
  padding: 2px 6px;
  border-radius: 6px;
  white-space: nowrap;
  letter-spacing: -0.2px;
  background: ${({ isPast }) => (isPast ? "rgba(43, 200, 102, 0.12)" : "rgba(255, 145, 35, 0.12)")};
  color: ${({ isPast }) => (isPast ? colors.successDark : "#e07800")};
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ isPast }) => (isPast ? colors.success : colors.occasionalOrange)};
  flex-shrink: 0;
  box-shadow: 0 0 0 2px ${({ isPast }) =>
    isPast ? "rgba(43, 200, 102, 0.2)" : "rgba(255, 145, 35, 0.2)"};
`;

/* ── Selected Detail ──────────────────────────────── */

const SelectedDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 14px 16px;
  background: ${colors.neutral};
  border: 1px solid ${colors.neutralDark};
  border-radius: 10px;
  animation: ${slideUp} 0.25s ease forwards;
`;

const DetailDate = styled.span`
  flex: 1;
  font-size: ${fonts.sizes.small};
  color: ${colors.night};
  text-transform: capitalize;
`;

const DetailAmount = styled.span`
  font-size: ${fonts.sizes.highlight};
  font-weight: ${fonts.weight.bold};
  color: ${({ isPast }) => (isPast ? colors.successDark : "#e07800")};
`;

const DetailStatus = styled.span`
  font-size: ${fonts.sizes.mini};
  font-weight: ${fonts.weight.bold};
  padding: 3px 8px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: ${({ isPast }) => (isPast ? "rgba(43, 200, 102, 0.12)" : "rgba(255, 145, 35, 0.12)")};
  color: ${({ isPast }) => (isPast ? colors.successDark : "#e07800")};
`;

/* ── Legend ────────────────────────────────────────── */

const Legend = styled(Row)`
  justify-content: center;
  gap: 24px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${colors.neutralDark};
`;

const LegendItem = styled(Row)`
  align-items: center;
  gap: 8px;
`;

const LegendLabel = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${colors.night};
`;
